import ReactDOM from "react-dom";
import { ImageEnum, imgBlob } from "../cssImg";
import styled from "styled-components";
import React, { PropsWithChildren, useEffect, useRef } from "react";
import useTheme from "../hooks/useTheme";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const StyledModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  width: 100%;
  height: 100%;
  .modal {
    border-radius: 16px;
    overflow: hidden;

    header {
      background: var(--alternative-section-bg);
      color: var(--alternative-text-color);
      padding: 16px;
      display: flex;
      gap: 16px;
      align-items: center;

      .fingers {
        height: 40px;
        width: 40px;
      }

      h2 {
        flex-grow: 1;
      }

      .close {
        background: transparent;
        border: 1px solid transparent;
        border-radius: 50px;
        cursor: pointer;
        padding: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: inherit;
        &:hover,
        &:focus-visible {
          border-color: currentColor;
        }
      }
    }
    section {
      background: var(--surface-bg-100);
      color: var(--icons-text-blue-primary);
      padding: 24px 16px;
    }
  }
`;
export function Modal(
  props: PropsWithChildren<{ title?: string; onClose?: { (): void } }>
) {
  const { title, onClose, children } = props;
  const [, rawTheme] = useTheme();
  const iconColor = rawTheme === "high-contrast" ? "#110052" : "#FFFFFF";
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const getFocusable = () =>
      Array.from(
        modalRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ??
          []
      );

    getFocusable()[0]?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose?.();
        return;
      }
      if (e.key !== "Tab") return;
      const list = getFocusable();
      if (list.length === 0) {
        e.preventDefault();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const insideModal = !!(active && modalRef.current?.contains(active));
      if (e.shiftKey) {
        if (!insideModal || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (!insideModal || active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <StyledModal aria-modal="true" role="dialog">
      <div className="modal" ref={modalRef}>
        <header>
          <img
            className="fingers"
            src={imgBlob(ImageEnum.logoFingers, { "#000000": iconColor })}
            alt="SignUp Fingers Logo"
          />
          <h2>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="close"
            aria-label="Close"
          >
            <img
              src={imgBlob(ImageEnum.close, { white: iconColor })}
              alt=""
            />
          </button>
        </header>
        <section>{children}</section>
      </div>
    </StyledModal>,
    document.querySelector("body")
  );
}
